import React from "react";
import { ArrowLeft, ArrowRight, Save, Download, FileDown, CheckCircle2, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import ContactSection from "@/components/resume-builder/ContactSection";
import { SECTION_REGISTRY } from "@/components/resume-builder/sectionRegistry";
import AddSectionsBar from "@/components/resume-builder/AddSectionsBar";
import ResumePreview from "@/components/resume-builder/ResumePreview";
import AtsScorePanel from "@/components/resume-builder/AtsScorePanel";
import TemplateColorPicker from "@/components/resume-builder/TemplateColorPicker";
import PageSizeSelector from "@/components/resume-builder/PageSizeSelector";
import WizardStepper from "@/components/resume-builder/WizardStepper";
import AiToolsPanel from "@/components/resume-builder/AiToolsPanel";
import VideoIntroCard from "@/components/resume-builder/VideoIntroCard";

const WIZARD_STEPS = [
  { id: 1, label: "Basics" },
  { id: 2, label: "Experience & Education" },
  { id: 3, label: "Skills & Extras" },
  { id: 4, label: "AI Tools" },
  { id: 5, label: "Review & Download" },
];

const STEP2_KEYS = ["experience", "education", "projects", "internships", "certifications"];
const STEP3_MAIN_KEYS = ["skills", "languages"];
const STEP3_EXTRA_KEYS = ["websites", "hobbies", "extracurricular"];

/** Guided, FlowCV-style 5-step wizard for the Resume Builder — an alternative
 * to the single-page Advanced editor. Live preview stays visible on the right
 * at every step so users see their resume take shape as they go. */
export default function ResumeWizard({
  draft, setDraft, revealedExtras, setRevealedExtras, onBrowseAll,
  step, setStep, saving, saveDraft, finalizing, finalize, finalizeAtsResult, setActiveVersionId,
}) {
  const goNext = () => { saveDraft(); setStep(Math.min(5, step + 1)); };
  const goBack = () => setStep(Math.max(1, step - 1));

  return (
    <div data-testid="resume-wizard">
      <WizardStepper steps={WIZARD_STEPS} currentStep={step} onStepClick={setStep} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <div className="space-y-4" data-testid={`resume-wizard-step-${step}-panel`}>
          {step === 1 && (
            <>
              <ContactSection draft={draft} setDraft={setDraft} />
              {SECTION_REGISTRY.summary.render({ draft, setDraft })}
              <VideoIntroCard draft={draft} setDraft={setDraft} />
              <TemplateColorPicker draft={draft} setDraft={setDraft} onBrowseAll={onBrowseAll} />
            </>
          )}

          {step === 2 && STEP2_KEYS.map((key) => (
            <div key={key}>{SECTION_REGISTRY[key].render({ draft, setDraft })}</div>
          ))}

          {step === 3 && (
            <>
              {STEP3_MAIN_KEYS.map((key) => (
                <div key={key}>{SECTION_REGISTRY[key].render({ draft, setDraft })}</div>
              ))}
              <AddSectionsBar
                draft={draft} setDraft={setDraft} revealed={revealedExtras}
                onReveal={(key) => setRevealedExtras((prev) => new Set(prev).add(key))}
              />
              {STEP3_EXTRA_KEYS.filter((k) => revealedExtras.has(k)).map((key) => (
                <div key={key}>{SECTION_REGISTRY[key].render({ draft, setDraft })}</div>
              ))}
              {SECTION_REGISTRY.custom_sections.render({ draft, setDraft })}
            </>
          )}

          {step === 4 && (
            <AiToolsPanel draft={draft} setDraft={setDraft} setActiveVersionId={setActiveVersionId} />
          )}

          {step === 5 && (
            <>
              <AtsScorePanel draft={draft} setDraft={setDraft} presetResult={finalizeAtsResult} />
              <PageSizeSelector draft={draft} setDraft={setDraft} />
              <div className="glass-panel rounded-2xl p-4 flex flex-wrap items-center gap-3">
                <button onClick={saveDraft} disabled={saving} data-testid="resume-save-draft-button" className="flex items-center gap-1.5 bg-white/10 hover:bg-white/15 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Draft
                </button>
                <a href={`${API}/resume-builder/pdf?template=${draft.template}&template_color=${draft.template_color}&page_size=${draft.page_size || "A4"}`} target="_blank" rel="noopener noreferrer" data-testid="resume-download-pdf-button" className="flex items-center gap-1.5 bg-indigo-500 hover:bg-indigo-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors">
                  <Download className="w-3.5 h-3.5" /> Download PDF
                </a>
                <a href={`${API}/resume-builder/docx?template_color=${draft.template_color}&page_size=${draft.page_size || "A4"}`} target="_blank" rel="noopener noreferrer" data-testid="resume-download-docx-button" className="flex items-center gap-1.5 bg-white/10 hover:bg-white/15 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors">
                  <FileDown className="w-3.5 h-3.5" /> Download Word
                </a>
                <button onClick={finalize} disabled={finalizing} data-testid="resume-finalize-button" className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors">
                  {finalizing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Finalize & Use for Job Matching
                </button>
              </div>
            </>
          )}
        </div>

        <div className="lg:sticky lg:top-6 self-start">
          <ResumePreview draft={draft} />
        </div>
      </div>

      <div className="flex items-center justify-between mt-6">
        <button
          type="button" onClick={goBack} disabled={step === 1} data-testid="resume-wizard-back-button"
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        {step < 5 ? (
          <button
            type="button" onClick={goNext} data-testid="resume-wizard-next-button"
            className="flex items-center gap-1.5 bg-indigo-500 hover:bg-indigo-400 text-white text-xs font-semibold px-5 py-2.5 rounded-lg transition-colors"
          >
            Save &amp; Continue <ArrowRight className="w-3.5 h-3.5" />
          </button>
        ) : (
          <p className="text-xs text-slate-500" data-testid="resume-wizard-complete-hint">You're all set — download or finalize on the left.</p>
        )}
      </div>
    </div>
  );
}

